import { useState, useMemo } from 'react';
import { Plus, Loader2, Receipt, TrendingUp, Download } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { formatMoney } from '@/lib/money';
import { toCsv, downloadCsv } from '@/lib/csv';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { useMiscLedger, useCreateMiscLedger } from '@/hooks/api/useMiscLedger';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import {
  MiscLedgerDirection,
  MiscLedgerCategory,
  type MiscLedgerEntry,
} from '@/services/api/frontdeskOps.types';

// ─── Constants ───────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<MiscLedgerCategory, string> = {
  [MiscLedgerCategory.PETTY_CASH]: 'Petty Cash',
  [MiscLedgerCategory.SUPPLIES]: 'Supplies',
  [MiscLedgerCategory.MAINTENANCE]: 'Maintenance',
  [MiscLedgerCategory.GUEST_SERVICE]: 'Guest Service',
  [MiscLedgerCategory.COMMISSION]: 'Commission',
  [MiscLedgerCategory.OTHER]: 'Other',
};

interface DirectionConfig {
  title: string;
  description: string;
  icon: typeof Receipt;
  addLabel: string;
  amountClass: string;
  emptyTitle: string;
}

const CONFIG: Record<MiscLedgerDirection, DirectionConfig> = {
  [MiscLedgerDirection.EXPENSE]: {
    title: 'Misc Expense',
    description: 'Miscellaneous expenses logged at this property',
    icon: Receipt,
    addLabel: 'Add Expense',
    amountClass: 'text-gray-900',
    emptyTitle: 'No expenses logged',
  },
  [MiscLedgerDirection.REVENUE]: {
    title: 'Misc Revenue',
    description: 'Miscellaneous revenue logged at this property',
    icon: TrendingUp,
    addLabel: 'Add Revenue',
    amountClass: 'text-green-700',
    emptyTitle: 'No revenue logged',
  },
};

function fmtDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function MiscLedgerPage({ direction }: { direction: MiscLedgerDirection }) {
  const cfg = CONFIG[direction];
  const Icon = cfg.icon;
  const propertyId = useSelectedPropertyId();

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const [showAdd, setShowAdd] = useState(false);

  const listQuery = useMiscLedger(
    {
      propertyId,
      direction,
      from: fromDate || undefined,
      to: toDate || undefined,
      category: (filterCategory as MiscLedgerCategory) || undefined,
      page,
      limit: PAGE_SIZE,
    },
    !!propertyId,
  );

  const allItems: MiscLedgerEntry[] = listQuery.data?.data ?? [];
  const totalPages = listQuery.data?.meta?.totalPages ?? 1;

  const items = useMemo(() => {
    if (!search.trim()) return allItems;
    const q = search.toLowerCase();
    return allItems.filter((e) => (e.note ?? '').toLowerCase().includes(q));
  }, [allItems, search]);

  function handleExportCsv() {
    const exportRows = items.map((e) => ({
      date: fmtDate(e.entryDate),
      type: CATEGORY_LABELS[e.category] ?? e.category,
      amount: e.amount,
      note: e.note ?? '',
      createdBy: e.createdByName ?? '',
    }));
    const csv = toCsv(exportRows, [
      { key: 'date', header: 'Date' },
      { key: 'type', header: 'Type' },
      { key: 'amount', header: 'Amount' },
      { key: 'note', header: 'Note' },
      { key: 'createdBy', header: 'Created By' },
    ]);
    const slug = cfg.title.toLowerCase().replace(/\s+/g, '-');
    downloadCsv(`${slug}-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  }

  return (
    <div>
      <PageHeader
        title={cfg.title}
        description={cfg.description}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={handleExportCsv}
              disabled={!propertyId || items.length === 0}
            >
              <Download size={16} aria-hidden="true" />
              Export CSV
            </Button>
            <Button onClick={() => setShowAdd(true)} disabled={!propertyId}>
              <Plus size={16} aria-hidden="true" />
              {cfg.addLabel}
            </Button>
          </>
        }
      />

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select
          value={filterCategory}
          onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          aria-label="Filter by type"
        >
          <option value="">All Types</option>
          {(Object.entries(CATEGORY_LABELS) as [MiscLedgerCategory, string][]).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search notes..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-56"
          aria-label="Search notes"
        />
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="From date"
          />
          <span className="text-gray-400 text-sm">to</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="To date"
          />
        </div>
      </div>

      {/* Table */}
      {!propertyId ? (
        <EmptyState icon={Icon} title="Select a property" description="Select a property to continue." />
      ) : listQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : listQuery.isError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(listQuery.error)}
        </p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Icon}
          title={cfg.emptyTitle}
          description="Add an entry to get started."
          actionLabel={cfg.addLabel}
          onAction={() => setShowAdd(true)}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Date</Th>
                  <Th>Type</Th>
                  <Th>Amount</Th>
                  <Th>Note</Th>
                  <Th>Created By</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((e) => (
                  <tr key={e.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{fmtDate(e.entryDate)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{CATEGORY_LABELS[e.category] ?? e.category}</td>
                    <td className={cn('px-4 py-3 text-sm font-medium', cfg.amountClass)}>{formatMoney(e.amount)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{e.note ?? '--'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{e.createdByName ?? '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <SlideOver isOpen={showAdd} onClose={() => setShowAdd(false)} title={cfg.addLabel}>
        <AddMiscLedgerForm
          direction={direction}
          propertyId={propertyId}
          onClose={() => setShowAdd(false)}
        />
      </SlideOver>
    </div>
  );
}

// ─── Add form ────────────────────────────────────────────────────────────────

function AddMiscLedgerForm({
  direction,
  propertyId,
  onClose,
}: {
  direction: MiscLedgerDirection;
  propertyId: string;
  onClose: () => void;
}) {
  const [entryDate, setEntryDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [category, setCategory] = useState<MiscLedgerCategory>(MiscLedgerCategory.OTHER);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  const createMutation = useCreateMiscLedger();
  const parsedAmount = parseFloat(amount);
  const isValid = !!propertyId && !!entryDate && Number.isFinite(parsedAmount) && parsedAmount >= 0.01;

  function clearError() {
    if (createMutation.isError) createMutation.reset();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    createMutation.mutate(
      {
        propertyId,
        direction,
        category,
        amount: parsedAmount,
        entryDate,
        ...(note.trim() ? { note: note.trim() } : {}),
      },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="ml-date" className="block text-sm font-medium text-gray-700 mb-1">
          Date <span className="text-red-500">*</span>
        </label>
        <input
          id="ml-date"
          type="date"
          required
          value={entryDate}
          onChange={(e) => { setEntryDate(e.target.value); clearError(); }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div>
        <label htmlFor="ml-category" className="block text-sm font-medium text-gray-700 mb-1">
          Type <span className="text-red-500">*</span>
        </label>
        <select
          id="ml-category"
          value={category}
          onChange={(e) => setCategory(e.target.value as MiscLedgerCategory)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {(Object.entries(CATEGORY_LABELS) as [MiscLedgerCategory, string][]).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="ml-amount" className="block text-sm font-medium text-gray-700 mb-1">
          Amount <span className="text-red-500">*</span>
        </label>
        <input
          id="ml-amount"
          type="number"
          step="0.01"
          min="0.01"
          required
          value={amount}
          onChange={(e) => { setAmount(e.target.value); clearError(); }}
          placeholder="0.00"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div>
        <label htmlFor="ml-note" className="block text-sm font-medium text-gray-700 mb-1">Note</label>
        <textarea
          id="ml-note"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      {createMutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(createMutation.error)}
        </p>
      )}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={createMutation.isPending} disabled={!isValid}>
          {CONFIG[direction].addLabel}
        </Button>
      </div>
    </form>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}
